import { sameLocalModelBinding } from '@/00-storage/service/local-model-references';
import { configureRpcFeature } from '@/features/naidan-rpc-integration/runtime/feature';
import { canInitializeModelLaunchDefaults, type ModelLaunchDefaultSnapshot } from '@/features/llama-cpp-browser/model-launch/defaults';
import { llamaCppBrowserService } from '@/features/llama-cpp-browser';
import { ref, readonly, computed, watch, type ComputedRef, type Ref } from 'vue';
import {
  ensureStrings,
  prepareLocale,
  resolveBrowserLocale,
  setLocale as setStringLocale,
} from '@/strings';
import {
  type Settings,
  type Endpoint,
  type EndpointType,
  DEFAULT_SETTINGS,
  EMPTY_LM_PARAMETERS,
  type Reasoning,
  type StorageType,
  type ProviderProfile,
  type UiLocale,
} from '@/01-models/types';
import { storageService } from '@/00-storage/service';
import { applyLocalModelPromotion, normalizeSavedLocalModelFields, type LocalModelPromotionFields } from '@/features/llama-cpp-browser/model-reference-promotion';
import { checkOPFSSupport } from '@/utils/opfs-detection';
import { STORAGE_BOOTSTRAP_KEY } from '@/constants';
import { loadLmProvider, prefetchLmProvider } from '@/features/lm/providerFactory';
import type { FakeLmDebugModeStatus } from '@/features/fake-lm';
import { prefetchFakeLmRuntime } from '@/features/lm/fetchFactory';
import { transformersJsService } from '@/features/transformers-js';
import { scheduleIdleTask, type ScheduledIdleTask } from '@/utils/idle-task';
import {
  parseBootstrapStorageTypeOverride,
  readStoredBootstrapStorageType,
} from '@/00-storage/service/bootstrap-storage-type';
import { useGlobalEvents } from './useGlobalEvents';
import { useConfirm } from './useConfirm';
import { areEndpointsEqual, cloneEndpoint, isConfiguredEndpoint, isHttpEndpoint, isSupportedEndpoint } from '@/01-models/endpoint';

const _settings = ref<Settings>({
  ...DEFAULT_SETTINGS,
  storageType: 'local',
  endpoint: { type: 'openai', url: '' },
} as Settings);

watch(() => _settings.value.experimental?.naidanRpc ?? 'disabled', status => {
  void configureRpcFeature({ status, settings: () => _settings.value }).catch(() => {
    console.warn('[naidan-rpc] stop-confirmation-failed');
  });
}, { immediate: true, flush: 'sync' });

let globalDefaultRevision = 0;
let unsavedGlobalDefaultRevision: number | undefined;
let settingsPublishedWithoutPrefetch: Settings | undefined;

function normalizeLocalSettings(): void {
  const current = _settings.value;
  const fields: LocalModelPromotionFields = { modelId: current.defaultModelId, titleGeneration: current.titleGeneration };
  normalizeSavedLocalModelFields({ target: fields, endpoint: current.endpoint });
  if (fields.titleGeneration !== undefined && fields.titleGeneration !== 'inherit'
    && (fields.modelId !== current.defaultModelId || fields.titleGeneration !== current.titleGeneration)) {
    _settings.value = { ...current, defaultModelId: fields.modelId, titleGeneration: fields.titleGeneration };
  }
}

watch(() => ({ endpoint: _settings.value.endpoint, modelId: _settings.value.defaultModelId, titleGeneration: _settings.value.titleGeneration }), normalizeLocalSettings, { flush: 'sync' });
storageService.subscribeLocalModelPromotions({
  listener: ({ promotion }) => {
    if (promotion.owner.kind !== 'global' || !promotion.isCurrent()) {
      return;
    }
    const current = _settings.value;
    const fields: LocalModelPromotionFields = { modelId: current.defaultModelId, titleGeneration: current.titleGeneration };
    applyLocalModelPromotion({ target: fields, endpoint: current.endpoint, changes: promotion.changes });
    if (fields.titleGeneration !== undefined && fields.titleGeneration !== 'inherit') {
      _settings.value = { ...current, defaultModelId: fields.modelId, titleGeneration: fields.titleGeneration };
    }
  },
});
watch(() => ({ endpoint: cloneEndpoint({ endpoint: _settings.value.endpoint }), modelId: _settings.value.defaultModelId }), (next, previous) => {
  if (!sameLocalModelBinding({ left: next, right: previous })) {
    globalDefaultRevision++;
  }
}, { flush: 'sync' });

const _initialized = ref(false);
const _isOnboardingDismissed = ref(false);
const _onboardingDraft = ref<{ url: string, type: EndpointType, headers?: [string, string][], models: string[], selectedModel: string } | null>(null);
const availableModels = ref<string[]>([]);
const isFetchingModels = ref(false);
// Entry views can read settings without authorizing their saved connection.
// Explicit fetchModels requests remain scoped to the requested endpoint.
const automaticModelRefresh = ref<'background' | 'deferred'>('background');
let initialModelRefreshStarted = false;
let pendingModelRefresh = false;
let nextModelFetchRequestId = 0;
let latestModelFetchRequestId = 0;
let activeModelFetchCount = 0;

function canRefreshModelsAutomatically(): boolean {
  const status = automaticModelRefresh.value;
  switch (status) {
  case 'background': return true;
  case 'deferred': return false;
  default: {
    const exhaustive: never = status;
    throw new Error(String(exhaustive));
  }
  }
}

type GlobalSearchSettings = NonNullable<NonNullable<Settings['experimental']>['globalSearch']>;
export type SearchPreviewMode = NonNullable<GlobalSearchSettings['previewMode']>;
export type SearchContextSize = NonNullable<GlobalSearchSettings['previewContextSize']>;
type GlobalSearchScopeSetting = NonNullable<GlobalSearchSettings['scope']>;
type GlobalSearchRoleFilterSetting = NonNullable<GlobalSearchSettings['roleFilter']>;

const globalSearchScope = computed<GlobalSearchScopeSetting>(() => (
  _settings.value.experimental?.globalSearch?.scope ?? 'title_only'
));
const globalSearchRoleFilter = computed<GlobalSearchRoleFilterSetting>(() => (
  _settings.value.experimental?.globalSearch?.roleFilter ?? 'all'
));
const searchPreviewMode = computed<SearchPreviewMode>(() => (
  _settings.value.experimental?.globalSearch?.previewMode ?? 'always'
));
const searchContextSize = computed<SearchContextSize>(() => (
  _settings.value.experimental?.globalSearch?.previewContextSize ?? 2
));

interface UseSettingsApi {
  settings: Readonly<Ref<Settings>>,
  captureModelLaunchDefaults(): Promise<ModelLaunchDefaultSnapshot>,
  initializeModelLaunchDefaults({ modelId, expected }: { modelId: string, expected: ModelLaunchDefaultSnapshot }): Promise<'applied' | 'changed'>,
  captureGlobalModelBinding({ sourceModelId }: { sourceModelId: string }): Promise<ModelLaunchDefaultSnapshot | undefined>,
  promoteGlobalModelBinding({ modelId, expected }: { modelId: string, expected: ModelLaunchDefaultSnapshot }): Promise<'applied' | 'changed'>,
  promoteTemporaryModelDefault({ sourceModelId, modelId, isCurrent }: { sourceModelId: string, modelId: string, isCurrent(): boolean }): Promise<void>,
  synchronizeModelBindingSettings({ isCurrent }: { isCurrent: () => boolean }): Promise<'synchronized' | 'changed'>,
  initialized: Readonly<Ref<boolean>>,
  isOnboardingDismissed: ComputedRef<boolean>,
  onboardingDraft: Readonly<Ref<{ url: string, type: EndpointType, headers?: [string, string][], models: string[], selectedModel: string } | null>>,
  availableModels: Readonly<Ref<string[]>>,
  isFetchingModels: Readonly<Ref<boolean>>,
  globalSearchScope: ComputedRef<GlobalSearchScopeSetting>,
  globalSearchRoleFilter: ComputedRef<GlobalSearchRoleFilterSetting>,
  searchPreviewMode: ComputedRef<SearchPreviewMode>,
  searchContextSize: ComputedRef<SearchContextSize>,
  init: ({ storageTypeOverride, dataZipBase64, modelRefresh }: { storageTypeOverride: string | undefined, dataZipBase64: string | undefined, modelRefresh: 'background' | 'deferred' }) => Promise<void>,
  deferModelRefresh(): void,
  resumeModelRefresh(): void,
  save: ({ patch, modelRefresh }: {
    patch: Partial<Settings>,
    modelRefresh: 'await' | 'background',
  }) => Promise<void>,
  captureExperimentalStorage(): () => boolean,
  updateExperimentalForStorage: ({ isCurrent, updater }: {
    isCurrent(): boolean,
    updater: ({ experimental }: { experimental: Settings['experimental'] }) => Settings['experimental'],
  }) => Promise<'saved' | 'changed'>,
  updateExperimental: ({ updater }: {
    updater: ({ experimental }: { experimental: Settings['experimental'] }) => Settings['experimental'],
  }) => Promise<void>,
  fetchModels: ({ overrides }: { overrides?: Endpoint }) => Promise<string[]>,
  updateProviderProfiles: ({ profiles }: { profiles: ProviderProfile[] }) => Promise<void>,
  updateGlobalModel: ({ modelId }: { modelId: string }) => Promise<void>,
  updateGlobalReasoningEffort: ({ effort }: { effort: Reasoning['effort'] }) => Promise<void>,
  commitGlobalModelConnection: ({ endpoint, modelId, isCurrent }: { endpoint: Endpoint, modelId: string, isCurrent(): boolean }) => Promise<'applied' | 'changed'>,
  updateGlobalModelAndEndpoint: ({ endpoint, modelId, expected }: {
    endpoint: Endpoint, modelId: string, expected: { endpoint: Endpoint, modelId: string | undefined },
  }) => Promise<'applied' | 'changed'>,
  updateGlobalEndpoint: ({ endpoint }: { endpoint: Endpoint }) => Promise<void>,
  updateSystemPrompt: ({ prompt }: { prompt: string }) => Promise<void>,
  updateStorageType: ({ type }: { type: StorageType }) => Promise<void>,
  setIsOnboardingDismissed: ({ dismissed }: { dismissed: boolean }) => void,
  setOnboardingDraft: ({ draft }: { draft: { url: string, type: EndpointType, headers?: [string, string][], models: string[], selectedModel: string } | null }) => void,
  setHeavyContentAlertDismissed: ({ dismissed }: { dismissed: boolean }) => void,
  setFakeLmDebugModeStatus: ({ status }: { status: FakeLmDebugModeStatus }) => Promise<void>,
  setLocale: ({ locale }: { locale: UiLocale }) => Promise<void>,
  setGlobalSearchScope: ({ scope }: { scope: GlobalSearchScopeSetting }) => Promise<void>,
  setGlobalSearchRoleFilter: ({ roleFilter }: { roleFilter: GlobalSearchRoleFilterSetting }) => Promise<void>,
  setSearchPreviewMode: ({ mode }: { mode: SearchPreviewMode }) => Promise<void>,
  setSearchContextSize: ({ size }: { size: SearchContextSize }) => Promise<void>,
  TEST_ONLY: {
    __testOnlyReset: () => void,
    __testOnlySetSettings: ({ newSettings }: { newSettings: Settings }) => void,
  },
}

let initPromise: Promise<void> | null = null;
let localeChangeQueue: Promise<void> = Promise.resolve();
let scheduledFakeLmRuntimePrefetch: ScheduledIdleTask | undefined;

// --- Synchronization ---

storageService.subscribeToChanges({
  listener: async ({ event }) => {
    if (event.type === 'settings' || event.type === 'migration') {
      try {
        const fresh = await storageService.loadSettings();
        if (fresh) {
          if (!canRefreshModelsAutomatically() && !areEndpointsEqual({ left: _settings.value.endpoint, right: fresh.endpoint })) {
            pendingModelRefresh = true;
          }
          _settings.value = fresh;
          scheduleFakeLmRuntimePrefetch({ status: fresh.experimental?.fakeLm ?? 'disabled' });
          await setStringLocale({
            locale: fresh.experimental?.locale ?? resolveBrowserLocale(),
          });
        } else {
          const type = event.type;
          switch (type) {
          case 'settings': break;
          case 'migration':
          // Clearing storage still retires editor write owners. Republish the
          // current UI settings so mounted editors can capture the new provider
          // boundary even when no saved Settings record exists yet.
            _settings.value = { ..._settings.value };
            break;
          default: {
            const unreachable: never = type;
            throw new Error(String(unreachable));
          }
          }
        }
      } catch (error) {
        console.error('Failed to synchronize settings:', error);
      }
    }
  },
});

// Performance-only watcher: this does not maintain application correctness.
// It only prefetches the selected provider during idle time to reduce the
// latency of the first request.
watch(
  () => ({
    initialized: _initialized.value,
    settings: _settings.value,
    endpoint: _settings.value.endpoint,
    modelRefresh: canRefreshModelsAutomatically(),
  }),
  ({ initialized, settings, endpoint, modelRefresh }, _previous, onCleanup) => {
    if (settings === settingsPublishedWithoutPrefetch) {
      settingsPublishedWithoutPrefetch = undefined;
      return;
    }
    if (!initialized || !modelRefresh || !isSupportedEndpoint(endpoint)) {
      return;
    }

    const scheduled = scheduleIdleTask({
      task: async () => {
        if (!canRefreshModelsAutomatically()) {
          return;
        }
        await prefetchLmProvider({ endpointType: endpoint.type });
      },
      timeoutMs: 2_000,
      fallbackDelayMs: 500,
    });

    onCleanup(() => {
      scheduled.cancel();
    });
  },
  { immediate: true },
);

watch(
  () => _settings.value.endpoint.type,
  (endpointType, _previousType, onCleanup) => {
    switch (endpointType) {
    case 'naidan_rpc':
    case 'openai':
    case 'ollama':
      return;
    case 'llama_cpp_browser':
    case 'transformers_js':
      break;
    case 'browser_provided_lm':
    case 'unsupported_experimental_endpoint':
      return;
    default: {
      const _ex: never = endpointType;
      throw new Error(`Unhandled endpoint type: ${_ex}`);
    }
    }

    const modelService = (() => {
      switch (endpointType) {
      case 'llama_cpp_browser': return llamaCppBrowserService;
      case 'transformers_js': return transformersJsService;
      default: {
        const exhaustive: never = endpointType;
        throw new Error(`Unhandled endpoint: ${exhaustive}`);
      }
      }
    })();
    const unsubscribe = modelService.subscribeModelList({
      listener: async () => {
        if (!canRefreshModelsAutomatically()) {
          pendingModelRefresh = true;
          return;
        }
        const { fetchModels } = useSettings();
        try {
          await fetchModels({});
        } catch {
        // fetchModels records the relevant error. Subscription callbacks must not
        // create unhandled promise rejections when a background refresh fails.
        }
      },
    });
    onCleanup(() => {
      unsubscribe();
    });
  },
  { immediate: true },
);

function scheduleFakeLmRuntimePrefetch({ status }: {
  status: FakeLmDebugModeStatus,
}): void {
  scheduledFakeLmRuntimePrefetch?.cancel();
  scheduledFakeLmRuntimePrefetch = undefined;
  if (!canRefreshModelsAutomatically()) {
    return;
  }

  switch (status) {
  case 'enabled': {
    scheduledFakeLmRuntimePrefetch = scheduleIdleTask({
      task: async () => {
        if (!canRefreshModelsAutomatically()) {
          return;
        }
        await prefetchFakeLmRuntime({ status });
      },
      timeoutMs: 2_000,
      fallbackDelayMs: 500,
    });
    break;
  }
  case 'disabled':
    break;
  default: {
    const _ex: never = status;
    throw new Error(`Unhandled fake LM debug mode status: ${_ex}`);
  }
  }
}

export function useSettings(): UseSettingsApi {
  const loading = ref(false);

  const isOnboardingDismissed = computed(() => {
    const endpoint = _settings.value.endpoint;
    const hasPersistedUnsupportedEndpoint = endpoint.type === 'unsupported_experimental_endpoint';
    const hasEndpoint = isConfiguredEndpoint({ endpoint });
    const hasModel = !!_settings.value.defaultModelId;
    return _isOnboardingDismissed.value
      || hasPersistedUnsupportedEndpoint
      || (hasEndpoint && hasModel);
  });

  async function init({ storageTypeOverride, dataZipBase64, modelRefresh }: { storageTypeOverride: string | undefined, dataZipBase64: string | undefined, modelRefresh: 'background' | 'deferred' }) {
    if (_initialized.value) {
      return;
    }
    if (initPromise) {
      return initPromise;
    }
    automaticModelRefresh.value = modelRefresh;
    console.log("storageTypeOverride", storageTypeOverride);

    initPromise = (async () => {
      loading.value = true;
      try {
        // Determine storage type from persisted flag
        const {
          rawSavedStorageType,
          parsedSavedStorageType,
        } = readStoredBootstrapStorageType();
        let bootstrapType: 'local' | 'opfs' | 'memory' | null = (() => {
          switch (parsedSavedStorageType.status) {
          case 'valid':
            return parsedSavedStorageType.savedStorageType;
          case 'invalid':
          case 'missing':
            return null;
          default: {
            const _ex: never = parsedSavedStorageType;
            throw new Error(`Unhandled stored bootstrap storage type status: ${((_ex satisfies never) as { readonly status: string }).status}`);
          }
          }
        })();

        if (storageTypeOverride) {
          if (rawSavedStorageType !== null) {
            if (storageTypeOverride !== rawSavedStorageType) {
              const { addInfoEvent } = useGlobalEvents();
              addInfoEvent({
                source: 'SettingsService',
                message: await ensureStrings.useSettings__storage_type_is_already_set_and_requested_type_was_ignored({
                  savedStorageType: rawSavedStorageType,
                  requestedStorageType: storageTypeOverride,
                }),
              });

              const { showConfirm } = useConfirm();
              // Do not await to avoid blocking initialization/mount
              showConfirm({
                title: await ensureStrings.useSettings__storage_already_initialized(),
                message: await ensureStrings.useSettings__request_to_use_storage_type_was_ignored({
                  savedStorageType: rawSavedStorageType,
                  requestedStorageType: storageTypeOverride,
                }),
                confirmButtonText: await ensureStrings.useSettings__ok(),
              });
            }
          } else {
            const parsedOverrideStorageType = parseBootstrapStorageTypeOverride({
              rawStorageType: storageTypeOverride,
            });
            switch (parsedOverrideStorageType.status) {
            case 'valid':
              bootstrapType = parsedOverrideStorageType.savedStorageType;
              break;
            case 'invalid':
              console.warn(`Invalid storage-type override: "${storageTypeOverride}". Ignoring.`);
              break;
            default: {
              const _ex: never = parsedOverrideStorageType;
              throw new Error(`Unhandled bootstrap storage type override status: ${((_ex satisfies never) as { readonly status: string }).status}`);
            }
            }
          }
        }

        if (rawSavedStorageType !== null && parsedSavedStorageType.status === 'invalid') {
          console.warn(`Invalid storage type found in localStorage: "${rawSavedStorageType}". Falling back to detection.`, parsedSavedStorageType.savedStorageTypeValidationError);
          const { addErrorEvent } = useGlobalEvents();
          addErrorEvent({
            source: 'SettingsService',
            message: await ensureStrings.useSettings__invalid_storage_type_falling_back_to_default_detection(),
            details: parsedSavedStorageType.savedStorageTypeValidationError,
          });
        }

        if (rawSavedStorageType === null && bootstrapType && typeof localStorage !== 'undefined') {
          localStorage.setItem(STORAGE_BOOTSTRAP_KEY, bootstrapType);
        }

        if (!bootstrapType) {
          // First run or cleared state: detect best available storage
          const isSupported = await checkOPFSSupport();
          bootstrapType = isSupported ? 'opfs' : 'local';

          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(STORAGE_BOOTSTRAP_KEY, bootstrapType);
          }
        }

        // Sync local settings ref with determined storage type
        _settings.value.storageType = bootstrapType;

        await storageService.init({ type: bootstrapType });

        // Handle URL-based data import BEFORE loading existing settings to ensure append mode works correctly
        if (dataZipBase64) {
          try {
            const { urlImportExportLogic } = await import('@/features/import-export/url-logic');
            await urlImportExportLogic.importFromBase64({ zipBase64: dataZipBase64 });
            // Clear local reference to large data to help GC
            dataZipBase64 = undefined;
            const { addInfoEvent } = useGlobalEvents();
            addInfoEvent({
              source: 'SettingsService',
              message: await ensureStrings.useSettings__data_successfully_imported_from_url(),
            });
          } catch (err) {
            console.error('Failed to import data from URL:', err);
            const { addErrorEvent } = useGlobalEvents();
            addErrorEvent({
              source: 'SettingsService',
              message: await ensureStrings.useSettings__failed_to_import_data_from_url(),
              details: err instanceof Error ? err : String(err),
            });
          }
        }

        const s = await storageService.loadSettings();
        if (s) {
          _settings.value = s;
          scheduleFakeLmRuntimePrefetch({ status: s.experimental?.fakeLm ?? 'disabled' });
          await setStringLocale({
            locale: s.experimental?.locale ?? resolveBrowserLocale(),
          });
        } else {
          // If no settings saved yet (new user), ensure defaults are clean but functional
          _settings.value.endpoint = { type: 'openai', url: '' };
          await setStringLocale({ locale: resolveBrowserLocale() });
        }
        flushAutomaticModelRefresh();
      } finally {
        loading.value = false;
        _initialized.value = true;
        initPromise = null;
      }
    })();

    return initPromise;
  }

  function deferModelRefresh(): void {
    automaticModelRefresh.value = 'deferred';
    scheduledFakeLmRuntimePrefetch?.cancel();
    scheduledFakeLmRuntimePrefetch = undefined;
  }

  function flushAutomaticModelRefresh(): void {
    if (!canRefreshModelsAutomatically() || (initialModelRefreshStarted && !pendingModelRefresh)) {
      return;
    }
    initialModelRefreshStarted = true;
    pendingModelRefresh = false;
    if (isConfiguredEndpoint({ endpoint: _settings.value.endpoint })) {
      void fetchModels({}).catch(() => {});
    }
  }

  function resumeModelRefresh(): void {
    const wasDeferred = !canRefreshModelsAutomatically();
    automaticModelRefresh.value = 'background';
    if (_initialized.value) {
      flushAutomaticModelRefresh();
      if (wasDeferred) {
        scheduleFakeLmRuntimePrefetch({ status: _settings.value.experimental?.fakeLm ?? 'disabled' });
      }
    }
  }

  async function fetchModels({ overrides }: { overrides?: Endpoint }): Promise<string[]> {
    const requestId = ++nextModelFetchRequestId;
    latestModelFetchRequestId = requestId;
    activeModelFetchCount += 1;
    isFetchingModels.value = true;

    const endpoint = overrides ?? _settings.value.endpoint;

    try {
      if (isHttpEndpoint(endpoint) && endpoint.url === '') {
        if (requestId === latestModelFetchRequestId) {
          availableModels.value = [];
        }
        return [];
      }

      const provider = await loadLmProvider({
        endpoint,
        fakeLmDebugModeStatus: _settings.value.experimental?.fakeLm ?? 'disabled',
      });

      const models = await provider.listModels({ signal: undefined });
      if (requestId === latestModelFetchRequestId) {
        availableModels.value = models;
      }
      return models;
    } catch (err) {
      if (requestId === latestModelFetchRequestId) {
        const { addErrorEvent } = useGlobalEvents();
        addErrorEvent({
          source: 'useSettings:fetchModels',
          message: await ensureStrings.useSettings__failed_to_fetch_models_for_settings(),
          details: err instanceof Error ? err : String(err),
        });
        console.error('Failed to fetch models:', err);
      }
      throw err;
    } finally {
      activeModelFetchCount -= 1;
      isFetchingModels.value = activeModelFetchCount > 0;
    }
  }

  async function save({ patch, modelRefresh }: {
    patch: Partial<Settings>,
    modelRefresh: 'await' | 'background',
  }) {
    if (Object.hasOwn(patch, 'endpoint') && patch.endpoint === undefined) {
      throw new Error('Global settings endpoint cannot be undefined');
    }

    const previousEndpoint = _settings.value.endpoint;
    const normalizedPatch: Partial<Settings> = patch.endpoint === undefined
      ? patch
      : { ...patch, endpoint: cloneEndpoint({ endpoint: patch.endpoint }) };

    // Update local reactive state
    _settings.value = { ..._settings.value, ...normalizedPatch };
    const writesDefault = normalizedPatch.endpoint !== undefined || Object.hasOwn(normalizedPatch, 'defaultModelId');
    const defaultRevision = globalDefaultRevision;
    if (writesDefault) {
      unsavedGlobalDefaultRevision = defaultRevision;
    }

    // If storage type is changed, handle provider switching/migration
    if (normalizedPatch.storageType && normalizedPatch.storageType !== storageService.getCurrentType()) {
      await storageService.switchProvider({ type: normalizedPatch.storageType });
    }

    // Persist as a patch to ensure we don't overwrite concurrent changes to other fields
    await storageService.updateSettings({
      updater: ({ current: curr }) => {
        const base = curr || _settings.value;
        return { ...base, ...normalizedPatch } as Settings;
      },
    });
    if (writesDefault && unsavedGlobalDefaultRevision === defaultRevision) {
      unsavedGlobalDefaultRevision = undefined;
    }

    // Re-fetch models if connection changed
    const endpointChanged = normalizedPatch.endpoint !== undefined
      && !areEndpointsEqual({ left: previousEndpoint, right: normalizedPatch.endpoint });
    if (endpointChanged && canRefreshModelsAutomatically()) {
      switch (modelRefresh) {
      case 'await':
        await fetchModels({});
        break;
      case 'background':
        // URL-provided connection settings must become usable before a network
        // model-list request completes. Waiting here would put endpoint latency
        // back on the onboarding critical path that this startup refactor removes.
        (async () => {
          try {
            await fetchModels({});
          } catch {
            // fetchModels already records the user-visible/global error details.
          }
        })();
        break;
      default: {
        const _ex: never = modelRefresh;
        return _ex;
      }
      }
    } else if (endpointChanged) {
      pendingModelRefresh = true;
    }
  }

  async function updateExperimental({
    updater,
  }: {
    updater: ({ experimental }: { experimental: Settings['experimental'] }) => Settings['experimental'],
  }): Promise<void> {
    let savedSettings: Settings | undefined;
    await storageService.updateSettings({
      updater: ({ current }) => {
        const base = current ?? _settings.value;
        savedSettings = {
          ...base,
          experimental: updater({ experimental: base.experimental }),
        };
        return savedSettings;
      },
    });

    if (savedSettings !== undefined) {
      _settings.value = savedSettings;
    }
  }

  function captureExperimentalStorage(): () => boolean {
    return storageService.captureSettingsStorage();
  }

  async function updateExperimentalForStorage({ isCurrent, updater }: {
    isCurrent(): boolean,
    updater: ({ experimental }: { experimental: Settings['experimental'] }) => Settings['experimental'],
  }): Promise<'saved' | 'changed'> {
    let savedSettings: Settings | undefined;
    const outcome = await storageService.updateSettingsForStorage({
      isCurrent,
      updater: ({ current }) => {
        const base = current ?? _settings.value;
        savedSettings = { ...base, experimental: updater({ experimental: base.experimental }) };
        return savedSettings;
      },
    });
    switch (outcome) {
    case 'changed': return outcome;
    case 'saved':
      if (!isCurrent()) {
        return 'changed';
      }
      if (savedSettings) {
        _settings.value = savedSettings;
      }
      return outcome;
    default: {
      const exhaustive: never = outcome;
      throw new Error(String(exhaustive));
    }
    }
  }

  // --- Explicit Actions ---

  async function updateProviderProfiles({ profiles }: { profiles: ProviderProfile[] }) {
    const patch = { providerProfiles: [...profiles] };
    _settings.value.providerProfiles = patch.providerProfiles;
    await storageService.updateSettings({ updater: ({ current: curr }) => ({ ...(curr || _settings.value), ...patch } as Settings) });
  }

  async function captureModelLaunchDefaults(): Promise<ModelLaunchDefaultSnapshot> {
    const isStorageCurrent = storageService.captureSettingsStorage();
    const snapshot = { endpoint: cloneEndpoint({ endpoint: _settings.value.endpoint }), modelId: _settings.value.defaultModelId, revision: globalDefaultRevision, isStorageCurrent };
    const stored = await storageService.loadSettings();
    const matches = stored === null || (areEndpointsEqual({ left: stored.endpoint, right: snapshot.endpoint }) && stored.defaultModelId === snapshot.modelId);
    return { ...snapshot, storedMatches: matches };
  }

  async function initializeModelLaunchDefaults({ modelId, expected }: { modelId: string, expected: ModelLaunchDefaultSnapshot }): Promise<'applied' | 'changed'> {
    const stillExpected = ({ current }: { current: Settings }): boolean => (
      expected.isStorageCurrent() && expected.revision === globalDefaultRevision
      && current.defaultModelId === expected.modelId
      && areEndpointsEqual({ left: current.endpoint, right: expected.endpoint })
      && canInitializeModelLaunchDefaults({ endpoint: current.endpoint, modelId: current.defaultModelId })
    );
    if (!expected.storedMatches || !stillExpected({ current: _settings.value })) {
      return 'changed';
    }
    let applied = false;
    const outcome = await storageService.updateSettingsForStorage({
      isCurrent: expected.isStorageCurrent,
      updater: ({ current }) => {
        const base = current ?? _settings.value;
        if (!stillExpected({ current: base })) {
          return base;
        }
        applied = true;
        return { ...base, endpoint: { type: 'llama_cpp_browser' }, defaultModelId: modelId };
      },
    });
    switch (outcome) {
    case 'changed': return 'changed';
    case 'saved': break;
    default: {
      const exhaustive: never = outcome;
      throw new Error(String(exhaustive));
    }
    }
    if (!applied || !expected.isStorageCurrent()) {
      return 'changed';
    }
    // A newer explicit in-memory selection must not be overwritten by the
    // completion of this write. It owns its own settings save.
    if (stillExpected({ current: _settings.value })) {
      _settings.value = { ..._settings.value, endpoint: { type: 'llama_cpp_browser' }, defaultModelId: modelId };
      if (!canRefreshModelsAutomatically()) {
        pendingModelRefresh = true;
      }
    }
    return 'applied';
  }

  async function captureGlobalModelBinding({ sourceModelId }: { sourceModelId: string }): Promise<ModelLaunchDefaultSnapshot | undefined> {
    const snapshot = await captureModelLaunchDefaults();
    if (!snapshot.storedMatches || !snapshot.isStorageCurrent() || snapshot.revision !== globalDefaultRevision
      || snapshot.endpoint.type !== 'llama_cpp_browser' || snapshot.modelId !== sourceModelId) {
      return undefined;
    }
    return snapshot;
  }

  async function synchronizeModelBindingSettings({ isCurrent }: { isCurrent: () => boolean }): Promise<'synchronized' | 'changed'> {
    if (!isCurrent() || unsavedGlobalDefaultRevision === globalDefaultRevision) {
      return 'changed';
    }
    const isStorageCurrent = storageService.captureSettingsStorage();
    const revision = globalDefaultRevision;
    const fresh = await storageService.loadSettings();
    if (!isCurrent() || !isStorageCurrent() || revision !== globalDefaultRevision
      || unsavedGlobalDefaultRevision === globalDefaultRevision) {
      return 'changed';
    }
    if (fresh === null) {
      // First use does not persist settings until something is changed. A
      // chat-scoped model can already be usable with this untouched global pair.
      return areEndpointsEqual({ left: _settings.value.endpoint, right: { type: 'openai', url: '' } })
        && !_settings.value.defaultModelId ? 'synchronized' : 'changed';
    }
    const endpointChanged = !areEndpointsEqual({ left: fresh.endpoint, right: _settings.value.endpoint });
    if (endpointChanged || fresh.defaultModelId !== _settings.value.defaultModelId) {
      // Admission refreshes the binding pair without authorizing connection work.
      _settings.value = { ..._settings.value, endpoint: cloneEndpoint({ endpoint: fresh.endpoint }), defaultModelId: fresh.defaultModelId };
      settingsPublishedWithoutPrefetch = _settings.value;
      if (endpointChanged && !canRefreshModelsAutomatically()) {
        pendingModelRefresh = true;
      }
    }
    return 'synchronized';
  }

  async function promoteGlobalModelBinding({ modelId, expected }: { modelId: string, expected: ModelLaunchDefaultSnapshot }): Promise<'applied' | 'changed'> {
    const hasPromotionLock = (): boolean => {
      const storageType = storageService.getCurrentType();
      switch (storageType) {
      case 'memory': return true;
      case 'local':
      case 'opfs':
        // Persistent promotion requires coordination with other tabs.
        return typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function';
      default: {
        const exhaustive: never = storageType;
        throw new Error(String(exhaustive));
      }
      }
    };
    const stillExpected = ({ current }: { current: Settings }): boolean => (
      expected.storedMatches && expected.isStorageCurrent() && expected.revision === globalDefaultRevision
      && hasPromotionLock()
      && expected.endpoint.type === 'llama_cpp_browser' && expected.modelId !== undefined
      && current.defaultModelId === expected.modelId
      && areEndpointsEqual({ left: current.endpoint, right: expected.endpoint })
    );
    if (!stillExpected({ current: _settings.value })) {
      return 'changed';
    }
    let applied = false;
    const outcome = await storageService.updateSettingsForStorage({
      isCurrent: expected.isStorageCurrent,
      updater: ({ current }) => {
        const base = current ?? _settings.value;
        if (!stillExpected({ current: base })) {
          return base;
        }
        applied = true;
        return { ...base, defaultModelId: modelId };
      },
    });
    switch (outcome) {
    case 'changed': return 'changed';
    case 'saved': break;
    default: {
      const exhaustive: never = outcome;
      throw new Error(String(exhaustive));
    }
    }
    if (!applied || !expected.isStorageCurrent()) {
      return 'changed';
    }
    if (stillExpected({ current: _settings.value })) {
      _settings.value = { ..._settings.value, defaultModelId: modelId };
    } else if (_settings.value.defaultModelId !== modelId || !areEndpointsEqual({ left: _settings.value.endpoint, right: expected.endpoint })) {
      return 'changed';
    }
    return 'applied';
  }

  async function promoteTemporaryModelDefault({ sourceModelId, modelId, isCurrent }: { sourceModelId: string, modelId: string, isCurrent(): boolean }): Promise<void> {
    let applied = false;
    await storageService.updateSettingsForStorage({
      isCurrent,
      updater: ({ current }) => {
        const base = current ?? _settings.value;
        if (base.endpoint.type !== 'llama_cpp_browser' || base.defaultModelId !== sourceModelId) {
          return base;
        }
        applied = true;
        return { ...base, defaultModelId: modelId };
      },
    });
    if (applied && isCurrent() && _settings.value.endpoint.type === 'llama_cpp_browser' && _settings.value.defaultModelId === sourceModelId) {
      _settings.value = { ..._settings.value, defaultModelId: modelId };
    }
  }

  async function updateGlobalModel({ modelId }: { modelId: string }) {
    _settings.value.defaultModelId = modelId;
    const revision = globalDefaultRevision;
    unsavedGlobalDefaultRevision = revision;
    await storageService.updateSettings({ updater: ({ current: curr }) => ({ ...(curr || _settings.value), defaultModelId: modelId }) });
    if (unsavedGlobalDefaultRevision === revision) {
      unsavedGlobalDefaultRevision = undefined;
    }
  }

  async function updateGlobalReasoningEffort({ effort }: { effort: Reasoning['effort'] }) {
    // Merge this single field against the latest persisted parameters. Do not
    // publish an unsaved value or replace concurrent edits to other parameters.
    await storageService.updateSettings({
      updater: ({ current }) => {
        const base = current ?? _settings.value;
        const parameters = base.lmParameters ?? EMPTY_LM_PARAMETERS;
        return { ...base, lmParameters: { ...parameters, reasoning: { ...parameters.reasoning, effort } } };
      },
    });
    const parameters = _settings.value.lmParameters ?? EMPTY_LM_PARAMETERS;
    _settings.value = { ..._settings.value, lmParameters: { ...parameters, reasoning: { ...parameters.reasoning, effort } } };
  }

  /** Apply an explicit connection choice without replacing unrelated settings. */
  async function commitGlobalModelConnection({ endpoint, modelId, isCurrent }: { endpoint: Endpoint, modelId: string, isCurrent(): boolean }): Promise<'applied' | 'changed'> {
    if (!isCurrent()) {
      return 'changed';
    }
    const nextEndpoint = cloneEndpoint({ endpoint });
    const outcome = await storageService.updateSettingsForStorage({
      isCurrent,
      updater: ({ current }) => ({ ...(current ?? _settings.value), endpoint: nextEndpoint, defaultModelId: modelId }),
    });
    switch (outcome) {
    case 'changed': return 'changed';
    case 'saved':
      if (!isCurrent()) {
        return 'changed';
      }
      if (!areEndpointsEqual({ left: _settings.value.endpoint, right: nextEndpoint })) {
        pendingModelRefresh = true;
      }
      _settings.value = { ..._settings.value, endpoint: nextEndpoint, defaultModelId: modelId };
      return 'applied';
    default: {
      const exhaustive: never = outcome;
      throw new Error(String(exhaustive));
    }
    }
  }

  /** Commit both default fields together, without optimistic half-updates. */
  async function updateGlobalModelAndEndpoint({ endpoint, modelId, expected }: {
    endpoint: Endpoint, modelId: string, expected: { endpoint: Endpoint, modelId: string | undefined },
  }): Promise<'applied' | 'changed'> {
    const matchesExpected = ({ current }: { current: Settings }): boolean => (
      current.defaultModelId === expected.modelId && areEndpointsEqual({ left: current.endpoint, right: expected.endpoint })
    );
    if (!matchesExpected({ current: _settings.value })) {
      return 'changed';
    }
    const nextEndpoint = cloneEndpoint({ endpoint });
    let applied = false;
    let observedDefault: { endpoint: Endpoint, modelId: string | undefined } | undefined;
    await storageService.updateSettings({
      updater: ({ current }) => {
        const base = current ?? _settings.value;
        // A dialog may be open while another settings action or browser tab saves.
        if (!matchesExpected({ current: base })) {
          observedDefault = { endpoint: cloneEndpoint({ endpoint: base.endpoint }), modelId: base.defaultModelId };
          return base;
        }
        applied = true;
        return { ...base, endpoint: nextEndpoint, defaultModelId: modelId };
      },
    });
    if (!applied) {
      // Show the newly observed default before requesting another confirmation.
      if (observedDefault) {
        _settings.value = { ..._settings.value, endpoint: observedDefault.endpoint, defaultModelId: observedDefault.modelId };
      }
      return 'changed';
    }
    _settings.value = { ..._settings.value, endpoint: nextEndpoint, defaultModelId: modelId };
    // Selection is already committed. A model-list failure must not report it
    // as a failed settings write. The local-model caller only lists local files.
    if (canRefreshModelsAutomatically()) {
      void fetchModels({}).catch(() => {});
    } else {
      pendingModelRefresh = true;
    }
    return 'applied';
  }

  async function updateGlobalEndpoint({ endpoint }: { endpoint: Endpoint }) {
    const previousEndpoint = _settings.value.endpoint;
    const nextEndpoint = cloneEndpoint({ endpoint });

    _settings.value.endpoint = nextEndpoint;
    const revision = globalDefaultRevision;
    unsavedGlobalDefaultRevision = revision;

    await storageService.updateSettings({
      updater: ({ current: curr }) => ({
        ...(curr || _settings.value),
        endpoint: cloneEndpoint({ endpoint: nextEndpoint }),
      }),
    });
    if (unsavedGlobalDefaultRevision === revision) {
      unsavedGlobalDefaultRevision = undefined;
    }

    if (!areEndpointsEqual({ left: previousEndpoint, right: nextEndpoint })) {
      await fetchModels({});
    }
  }

  async function updateSystemPrompt({ prompt }: { prompt: string }) {
    _settings.value.systemPrompt = prompt;
    await storageService.updateSettings({ updater: ({ current: curr }) => ({ ...(curr || _settings.value), systemPrompt: prompt }) });
  }

  async function updateStorageType({ type }: { type: StorageType }) {
    if (_settings.value.storageType === type) {
      return;
    }

    _settings.value.storageType = type;
    await storageService.switchProvider({ type });
    await storageService.updateSettings({ updater: ({ current: curr }) => ({ ...(curr || _settings.value), storageType: type }) });
  }

  function setIsOnboardingDismissed({ dismissed }: { dismissed: boolean }) {
    _isOnboardingDismissed.value = dismissed;
  }

  function setOnboardingDraft({ draft }: { draft: { url: string, type: EndpointType, headers?: [string, string][], models: string[], selectedModel: string } | null }) {
    _onboardingDraft.value = draft;
  }

  function setHeavyContentAlertDismissed({ dismissed }: { dismissed: boolean }) {
    _settings.value.heavyContentAlertDismissed = dismissed;
    storageService.updateSettings({ updater: ({ current: curr }) => ({ ...(curr || _settings.value), heavyContentAlertDismissed: dismissed }) });
  }

  async function setFakeLmDebugModeStatus({ status }: {
    status: FakeLmDebugModeStatus,
  }): Promise<void> {
    _settings.value.experimental = {
      ..._settings.value.experimental,
      fakeLm: status,
    };

    await storageService.updateSettings({
      updater: ({ current: curr }) => {
        const base = curr ?? _settings.value;
        return {
          ...base,
          experimental: {
            ...base.experimental,
            fakeLm: status,
          },
        };
      },
    });

    scheduleFakeLmRuntimePrefetch({ status });
  }

  async function setLocale({ locale }: {
    locale: UiLocale,
  }): Promise<void> {
    const operation = localeChangeQueue.catch(() => {
      // A failed locale change must not prevent a later explicit request.
    }).then(async () => {
      await prepareLocale({ locale });
      await updateExperimental({
        updater: ({ experimental }) => ({
          ...experimental,
          locale,
        }),
      });
      await setStringLocale({ locale });
    });
    localeChangeQueue = operation;
    await operation;
  }

  async function setGlobalSearchScope({ scope }: {
    scope: GlobalSearchScopeSetting,
  }): Promise<void> {
    await updateExperimental({
      updater: ({ experimental }) => ({
        ...experimental,
        globalSearch: {
          ...experimental?.globalSearch,
          scope,
        },
      }),
    });
  }

  async function setGlobalSearchRoleFilter({ roleFilter }: {
    roleFilter: GlobalSearchRoleFilterSetting,
  }): Promise<void> {
    await updateExperimental({
      updater: ({ experimental }) => ({
        ...experimental,
        globalSearch: {
          ...experimental?.globalSearch,
          roleFilter,
        },
      }),
    });
  }

  async function setSearchPreviewMode({ mode }: {
    mode: SearchPreviewMode,
  }): Promise<void> {
    await updateExperimental({
      updater: ({ experimental }) => ({
        ...experimental,
        globalSearch: {
          ...experimental?.globalSearch,
          previewMode: mode,
        },
      }),
    });
  }

  async function setSearchContextSize({ size }: {
    size: SearchContextSize,
  }): Promise<void> {
    await updateExperimental({
      updater: ({ experimental }) => ({
        ...experimental,
        globalSearch: {
          ...experimental?.globalSearch,
          previewContextSize: size,
        },
      }),
    });
  }

  function __testOnlySetSettings({ newSettings }: { newSettings: Settings }) {
    _settings.value = JSON.parse(JSON.stringify(newSettings));
  }

  function __testOnlyReset() {
    scheduledFakeLmRuntimePrefetch?.cancel();
    scheduledFakeLmRuntimePrefetch = undefined;
    _initialized.value = false;
    automaticModelRefresh.value = 'background';
    initialModelRefreshStarted = false;
    pendingModelRefresh = false;
    unsavedGlobalDefaultRevision = undefined;
    settingsPublishedWithoutPrefetch = undefined;
    _isOnboardingDismissed.value = false;
    _onboardingDraft.value = null;
    _settings.value = {
      ...DEFAULT_SETTINGS,
      storageType: 'local',
      endpoint: { type: 'openai', url: '' },
    } as Settings;
    availableModels.value = [];
    isFetchingModels.value = false;
    nextModelFetchRequestId = 0;
    latestModelFetchRequestId = 0;
    activeModelFetchCount = 0;
    initPromise = null;
    localeChangeQueue = Promise.resolve();
  }

  return {
    settings: readonly(_settings) as Readonly<Ref<Settings>>,
    captureModelLaunchDefaults,
    initializeModelLaunchDefaults,
    captureGlobalModelBinding,
    promoteGlobalModelBinding,
    promoteTemporaryModelDefault,
    synchronizeModelBindingSettings,
    initialized: readonly(_initialized),
    isOnboardingDismissed,
    onboardingDraft: readonly(_onboardingDraft) as Readonly<Ref<{ url: string, type: EndpointType, headers?: [string, string][], models: string[], selectedModel: string } | null>>,
    availableModels: readonly(availableModels) as Readonly<Ref<string[]>>,
    isFetchingModels: readonly(isFetchingModels),
    globalSearchScope,
    globalSearchRoleFilter,
    searchPreviewMode,
    searchContextSize,
    init,
    deferModelRefresh,
    resumeModelRefresh,
    save,
    updateExperimental,
    captureExperimentalStorage,
    updateExperimentalForStorage,
    fetchModels,
    updateProviderProfiles,
    updateGlobalModel,
    updateGlobalReasoningEffort,
    commitGlobalModelConnection,
    updateGlobalEndpoint,
    updateGlobalModelAndEndpoint,
    updateSystemPrompt,
    updateStorageType,
    setIsOnboardingDismissed,
    setOnboardingDraft,
    setHeavyContentAlertDismissed,
    setFakeLmDebugModeStatus,
    setLocale,
    setGlobalSearchScope,
    setGlobalSearchRoleFilter,
    setSearchPreviewMode,
    setSearchContextSize,
    ...((__BUILD_MODE_IS_TEST__ && {
      TEST_ONLY: {
        __testOnlyReset,
        __testOnlySetSettings,
      },
    }) || {}),
  };
}

// Export internal state and logic used only for testing here. Do not reference these in production logic.
// ESLint-required for TypeScript modules.
export const TEST_ONLY = {
};
